import React, { useState } from "react";

/**
 * Carrusel de fotos de un artículo: una imagen a la vez, flechas para
 * moverse y miniaturas abajo (estilo tienda en línea).
 *
 * Las fotos viven en Cloudinary con nombres predecibles:
 *   macuroArticulos/<id>      → foto principal
 *   macuroArticulos/<id>_1..4 → fotos adicionales
 * El componente intenta las 5 y se queda con las que existan; las que no,
 * se descartan solas con onError. Si no hay ninguna, muestra el placeholder.
 *
 * Las flechas y las miniaturas NO propagan el clic, para no disparar
 * acciones de la tarjeta que lo contiene.
 */
function CarruselFotos({ articulo, sinImagen, cloudBase, alto = 180 }) {
  const id = articulo?._id;
  const tieneFoto = articulo?.foto === "Si";

  const candidatas = tieneFoto && id && cloudBase
    ? [0, 1, 2, 3, 4].map(
        (s) => `${cloudBase}/image/upload/macuroArticulos/${id}${s === 0 ? "" : `_${s}`}.jpg`
      )
    : [];

  // null = aún no se sabe; true/false = existe o no
  const [estado, setEstado] = useState({});
  const [idx, setIdx] = useState(0);

  const marcar = (i, ok) =>
    setEstado((prev) => (prev[i] !== undefined ? prev : { ...prev, [i]: ok }));

  const validas = candidatas.filter((_, i) => estado[i] === true);
  const actual = validas[Math.min(idx, Math.max(0, validas.length - 1))] || sinImagen;

  const mover = (e, delta) => {
    e.stopPropagation();
    e.preventDefault();
    if (validas.length < 2) return;
    setIdx((prev) => {
      const n = prev + delta;
      if (n < 0) return validas.length - 1;
      if (n >= validas.length) return 0;
      return n;
    });
  };

  const flecha = {
    position: "absolute",
    top: "50%",
    transform: "translateY(-50%)",
    width: 26,
    height: 26,
    borderRadius: "50%",
    border: "none",
    background: "rgba(255,255,255,0.9)",
    boxShadow: "0 1px 4px rgba(0,0,0,0.25)",
    cursor: "pointer",
    fontSize: 13,
    lineHeight: "26px",
    padding: 0,
    color: "#333",
    zIndex: 2,
  };

  return (
    <div>
      {/* Precarga: define qué slots existen */}
      <div style={{ display: "none" }}>
        {candidatas.map((url, i) => (
          <img
            key={i}
            src={url}
            alt=""
            onLoad={() => marcar(i, true)}
            onError={() => marcar(i, false)}
          />
        ))}
      </div>

      <div style={{ position: "relative", backgroundColor: "#f4f4f4" }}>
        <img
          src={actual}
          alt={articulo?.nombre || ""}
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = sinImagen;
          }}
          style={{ width: "100%", height: alto, objectFit: "contain" }}
        />

        {validas.length > 1 && (
          <>
            <button
              type="button"
              onClick={(e) => mover(e, -1)}
              style={{ ...flecha, left: 6 }}
              title="Anterior"
            >
              ‹
            </button>
            <button
              type="button"
              onClick={(e) => mover(e, 1)}
              style={{ ...flecha, right: 6 }}
              title="Siguiente"
            >
              ›
            </button>
            <span
              style={{
                position: "absolute",
                bottom: 4,
                right: 6,
                background: "rgba(0,0,0,0.55)",
                color: "#fff",
                borderRadius: 10,
                fontSize: 10,
                padding: "1px 7px",
              }}
            >
              {Math.min(idx + 1, validas.length)}/{validas.length}
            </span>
          </>
        )}
      </div>

      {/* Miniaturas */}
      {validas.length > 1 && (
        <div style={{ display: "flex", gap: 4, padding: "4px 6px", flexWrap: "wrap" }}>
          {validas.map((url, i) => (
            <img
              key={url}
              src={url}
              alt=""
              onClick={(e) => {
                e.stopPropagation();
                setIdx(i);
              }}
              style={{
                width: 34,
                height: 34,
                objectFit: "contain",
                cursor: "pointer",
                background: "#fff",
                border: i === idx ? "2px solid #333" : "1px solid #ddd",
                borderRadius: 3,
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default CarruselFotos;
